import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { mtstatTrack } from '$/utils/mtstat';
import type { InputPanelRoleItem, InputPanelRoleScopeState, RoleTrackContext } from './types';
import { useRoleSelectCopy } from './useRoleSelectCopy';
import styles from './index.module.scss';

type RoleSelectPopupProps = {
  trackContext?: RoleTrackContext;
  roleList: InputPanelRoleItem[];
  selectedRoles: InputPanelRoleItem[];
  disabled?: boolean;
  onSelect: (roles: InputPanelRoleItem[]) => void;
  /** 可选远端分页状态；未提供时保留既有静态列表消费方。 */
  scopes?: Partial<Record<InputPanelRoleItem['scope'], InputPanelRoleScopeState>>;
  /** 登录或初始化前不发请求，由宿主解释暂不可选择的原因。 */
  unavailableMessage?: string;
  /** 自定义空态文案，房间可按个人、官方分别说明项目关联状态。 */
  emptyMessage?: string | Partial<Record<InputPanelRoleItem['scope'], string>>;
  /** 个人角色空态的创建入口，仅由需要该能力的宿主传入。 */
  onCreateRole?: () => void | Promise<void>;
  /** 创建角色进行中时禁用重复点击。 */
  creatingRole?: boolean;
};

/**
 * 角色与图片共用额度，达到上限后保留取消选择能力；完整弹层文案随语言资源更新。
 */
function RoleSelectPopup({
  trackContext,
  roleList,
  selectedRoles,
  disabled = false,
  onSelect,
  scopes,
  unavailableMessage,
  emptyMessage,
  onCreateRole,
  creatingRole = false,
}: RoleSelectPopupProps) {
  const copy = useRoleSelectCopy();
  const mounted = useRef(false);
  const createPending = useRef(false);
  const [creating, setCreating] = useState(false);
  const [creationFailed, setCreationFailed] = useState(false);
  const [activeScope, setActiveScope] = useState<InputPanelRoleItem['scope']>('personal');
  const selectedRoleIds = useMemo(
    () => new Set(selectedRoles.map((role) => role.id)),
    [selectedRoles],
  );
  const visibleRoles = useMemo(
    () => roleList.filter((role) => role.scope === activeScope),
    [activeScope, roleList],
  );
  const scopeState = scopes?.[activeScope];
  // 关闭弹层会将列表重置为 idle，退场期间不应把未请求状态显示为加载中。
  const initialLoading = scopeState?.status === 'loading';
  const scopeEmptyMessage = typeof emptyMessage === 'string' ? emptyMessage : emptyMessage?.[activeScope];

  /** 弹层卸载后只结束 Promise，不让迟到的创建错误影响其他页面或账号。 */
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  /** 创建失败留在当前空态重试；同步防重避免宿主 pending 回填前重复调用。 */
  const handleCreateRole = async () => {
    if (!onCreateRole || creatingRole || createPending.current) return;
    createPending.current = true;
    setCreating(true);
    setCreationFailed(false);
    try { await onCreateRole(); }
    catch { if (mounted.current) setCreationFailed(true); }
    finally {
      createPending.current = false;
      if (mounted.current) setCreating(false);
    }
  };

  /** 切换单个角色的多选状态，并回传完整已选列表。 */
  function handleRoleClick(role: InputPanelRoleItem) {
    if (trackContext) {
      mtstatTrack('roboneo_input_ip_pop_click', {
        location: trackContext.location,
        room_id: trackContext.roomId,
        tab: role.scope,
        ip_id: role.id,
      });
    }
    if (selectedRoleIds.has(role.id)) {
      onSelect(selectedRoles.filter((item) => item.id !== role.id));
      return;
    }
    onSelect([...selectedRoles, role]);
  }

  /** 只在用户点击 Tab 时上报，初始化不触发。 */
  function handleTabClick(tab: InputPanelRoleItem['scope']) {
    if (trackContext) {
      mtstatTrack('roboneo_input_ip_pop_tab_click', {
        location: trackContext.location,
        room_id: trackContext.roomId,
        tab,
      });
    }
    setActiveScope(tab);
  }

  /** 滚动距离底部 24px 内触发续页，让个人和官方列表分别维护自己的页码。 */
  function handleRoleListScroll(event: React.UIEvent<HTMLDivElement>) {
    const list = event.currentTarget;
    if (list.scrollHeight - list.scrollTop - list.clientHeight > 24
      || !scopeState || scopeState.status !== 'success' || scopeState.error
      || !scopeState.hasMore || scopeState.loadingMore) return;
    scopeState.loadMore();
  }

  return (
    <div className={styles.rolePopup}>
      <div className={styles.selectionPopupTitle}>{copy.title}</div>
      <div className={styles.roleTabs}>
        <button
          type="button"
          className={classNames(styles.roleTab, activeScope === 'personal' && styles.active)}
          onClick={() => handleTabClick('personal')}
        >
          {copy.personal}
        </button>
        <button
          type="button"
          className={classNames(styles.roleTab, activeScope === 'official' && styles.active)}
          onClick={() => handleTabClick('official')}
        >
          {copy.official}
        </button>
      </div>
      <div
        className={styles.roleOptionList}
        aria-busy={!unavailableMessage && (initialLoading || scopeState?.loadingMore)}
        onScroll={handleRoleListScroll}
      >
        {unavailableMessage ? <p className={styles.roleStatus} role="status">{unavailableMessage}</p> : null}
        {!unavailableMessage && initialLoading ? <p className={styles.roleStatus} role="status">{copy.loading}</p> : null}
        {!unavailableMessage && scopeState?.status === 'success' && !visibleRoles.length ? (
          <div className={styles.roleEmptyState}>
            <p role="status">
              {scopeEmptyMessage || (activeScope === 'personal' ? copy.personalEmpty : copy.officialEmpty)}
            </p>
            {activeScope === 'personal' && onCreateRole ? (
              <>
                {creationFailed && <p role="alert">{copy.createFailed}</p>}
                <button
                  type="button"
                  className={styles.roleCreateButton}
                  disabled={creatingRole || creating}
                  aria-busy={creatingRole || creating}
                  onClick={handleCreateRole}
                >
                  <IconFont name="plusBlack" fontSize={16} color="currentColor" />
                  <span>{creationFailed ? copy.retry : copy.createRole}</span>
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        {!unavailableMessage && scopeState?.error ? (
          <div className={styles.roleStatus} role="alert">
            <p>{copy.loadFailed}</p>
            <button type="button" disabled={initialLoading || scopeState.loadingMore} onClick={scopeState.status === 'error' ? scopeState.retry : scopeState.loadMore}>{copy.retry}</button>
          </div>
        ) : null}
        {!unavailableMessage && visibleRoles.map((role) => {
          const selected = selectedRoleIds.has(role.id);
          return (
            <button
              type="button"
              key={role.id}
              disabled={(disabled || initialLoading || scopeState?.status === 'error') && !selected}
              aria-pressed={selected}
              className={classNames(styles.roleOption, selected && styles.selected)}
              onClick={() => handleRoleClick(role)}
            >
              <span className={styles.roleAvatar}>
                {role.avatar ? (
                  <img src={role.avatar} alt="" crossOrigin="anonymous" />
                ) : null}
              </span>
              <span className={styles.roleOptionContent}>
                <span className={styles.selectionOptionName}>{role.name}</span>
                <span className={styles.selectionOptionDescription}>{role.description}</span>
              </span>
            </button>
          );
        })}
        {!unavailableMessage && scopeState?.loadingMore ? (
          <p className={styles.roleLoadingMore}>{copy.loading}</p>
        ) : null}
      </div>
    </div>
  );
}

export default RoleSelectPopup;
